import { useState, useEffect } from 'react';

const TOTAL_SECONDS = 40 * 60; // 40 minutes total

export interface HiddenMissionTimerResult {
  remainingSeconds: number;
  isExpired: boolean;
  display: {
    minutes: string;
    seconds: string;
    formatted: string; // "MM:SS"
  };
  progressPct: number; // 0–100, counts down
}

export function useHiddenMissionTimer(
  status: 'idle' | 'ongoing' | 'done',
  startedAt: number | null,
): HiddenMissionTimerResult {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (status !== 'ongoing') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);

  if (status !== 'ongoing' || startedAt === null) {
    const full = status === 'done' ? 0 : TOTAL_SECONDS;
    const mm = String(Math.floor(full / 60)).padStart(2, '0');
    const ss = String(full % 60).padStart(2, '0');
    return {
      remainingSeconds: full,
      isExpired: status === 'done',
      display: { minutes: mm, seconds: ss, formatted: `${mm}:${ss}` },
      progressPct: status === 'done' ? 0 : 100,
    };
  }

  const elapsed = Math.floor((now - startedAt) / 1000);
  const remaining = Math.max(0, TOTAL_SECONDS - elapsed);
  const isExpired = remaining === 0;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const progressPct = Math.round((remaining / TOTAL_SECONDS) * 100);

  return {
    remainingSeconds: remaining,
    isExpired,
    display: { minutes: mm, seconds: ss, formatted: `${mm}:${ss}` },
    progressPct,
  };
}
